import React, { useEffect, useState } from 'react';
import { useLiceos } from '../../../../application/liceos/hooks/useLiceos';
import ToolbarComponent from '../../../ui/section';
import Loader from '../../../ui/loader';
import './detail.css';

const UserDetailPage = (props) => {
    const {
        getUserById,
        getLiceoUsers,
        api: LiceosAPI,
        isLoading,
        error
    } = useLiceos();

    const liceoId = props.match?.params?.liceoId;
    const userId = props.match?.params?.userId;
    
    const [user, setUser] = useState(null);
    const [points, setPoints] = useState(null);
    const [learnwordsCourses, setLearnwordsCourses] = useState([]);
    const [learnwordsAvailable, setLearnwordsAvailable] = useState(true);
    const [learnwordsEligible, setLearnwordsEligible] = useState(true);
    const [loading, setLoading] = useState(true);
    const [localError, setLocalError] = useState(null);

    // Función para detectar si un ID es un UUID
    const isUUID = (id) => {
        if (!id || typeof id !== 'string') return false;
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return uuidRegex.test(id);
    };

    const extractKeycloakId = (u) => {
        if (!u) return null;
        return (
            u.keycloakId ||
            u.keycloak_id ||
            u.keycloakUserId ||
            u.keycloak_user_id ||
            u.keycloak?.id ||
            u.keycloak?.userId ||
            null
        );
    };

    const isLearnworldsEligible = (u) => {
        const t = getUserTypeValue(u);
        return ['estudiante', 'postulante', 'applicant', 'student'].includes(String(t || '').toLowerCase().trim());
    };

    const guessExpPointsSegmentId = (u) => {
        // Docs: DOCENTES, COACH, LICEOS (por defecto DOCENTES)
        const userType = String(getUserTypeValue(u) || '').toUpperCase().trim();
        const roles = Array.isArray(u?.roles) ? u.roles : (Array.isArray(u?.role) ? u.role : []);
        const rolesUpper = roles.map(r => String(r).toUpperCase());

        if (rolesUpper.includes('COACH') || userType === 'COACH') return 'COACH';
        if (rolesUpper.includes('LICEOS') || rolesUpper.includes('LICEAL')) return 'LICEOS';
        if (rolesUpper.includes('DOCENTE') || rolesUpper.includes('PROFESOR') || rolesUpper.includes('TEACHER')) return 'DOCENTES';

        // En este módulo también existen 'DOCENTE'/'DIRECTOR'. Los tratamos como DOCENTES.
        if (userType === 'DOCENTE' || userType === 'DIRECTOR') return 'DOCENTES';

        return 'DOCENTES';
    };

    useEffect(() => {
        if (!userId) return;
        
        const loadUserData = async () => {
            setLoading(true);
            setLocalError(null);
            try {
                let userData = null;
                
                // Si el ID es un UUID, buscar en la lista de usuarios del liceo
                if (isUUID(userId) && liceoId) {
                    console.log('🔍 ID es UUID, buscando en usuarios del liceo...');
                    const users = await getLiceoUsers(liceoId);
                    const userArray = Array.isArray(users) ? users : (users?.users || []);
                    userData = userArray.find(u => (u.id === userId || u._id === userId));
                    
                    if (!userData) {
                        console.error('❌ Usuario no encontrado en el liceo con UUID:', userId);
                        setLocalError('Usuario no encontrado');
                        setLoading(false);
                        return;
                    }
                } else {
                    // Si es ObjectId, usar getUserById normalmente
                    console.log('🔍 ID es ObjectId, usando getUserById...');
                    userData = await getUserById(userId);
                }

                // Nota: NO llamamos a /api/users/:id/detail porque el backend no expone esa ruta actualmente.
                // El detalle se construye con los datos que vienen desde getLiceoUsers + integraciones (ExpPoints/Learnworlds).

                // DEBUG (temporal): identificar campo de Keycloak ID y tipo en el payload del usuario
                try {
                    const candidateKeycloakId = extractKeycloakId(userData);
                    const candidateType = getUserTypeValue(userData);
                    const keys = userData && typeof userData === 'object' ? Object.keys(userData) : [];
                    // Evitar loguear objeto completo (puede tener datos sensibles)
                    console.log('🧩 [Liceos] Usuario keys:', keys);
                    console.log('🧩 [Liceos] Usuario keys (json):', JSON.stringify(keys));
                    console.log('🧩 [Liceos] Keycloak ID detectado:', candidateKeycloakId);
                    console.log('🧩 [Liceos] Attributes keys:', Object.keys(userData?.attributes || {}));
                    console.log(
                        '🧩 [Liceos] Tipo detectado:',
                        candidateType,
                        '| raw userType:',
                        userData?.userType,
                        '| raw type:',
                        userData?.type,
                        '| raw user_type:',
                        userData?.user_type,
                        '| raw userTypeName:',
                        userData?.userTypeName,
                        '| raw user_type_name:',
                        userData?.user_type_name,
                        '| raw profile:',
                        userData?.profile,
                        '| roles:',
                        userData?.roles
                    );
                } catch (e) {
                    // no-op
                }

                setUser(userData);

                // ==================== PUNTOS ====================
                // La doc indica ExpPoints con Keycloak User ID (UUID).
                // Preferimos ExpPoints y hacemos fallback a lo que venga en userData.
                let pointsData = null;
                const keycloakId = extractKeycloakId(userData);
                const segmentId = guessExpPointsSegmentId(userData);

                if (keycloakId) {
                    try {
                        const wallet = await LiceosAPI.getExpPointsWalletByUserId(keycloakId);
                        if (wallet && wallet.success === false) {
                            pointsData = null;
                        } else {
                            pointsData = {
                                ...wallet,
                                segmentId
                            };
                        }
                    } catch (err) {
                        // Si ExpPoints no está desplegado en este host, no debe romper la vista
                        console.warn('⚠️ ExpPoints no disponible:', err?.response?.status || err);
                        pointsData = null;
                    }
                }

                if (!pointsData) {
                    const localPoints =
                        userData?.wallet_points?.data?.balance ??
                        userData?.wallet_points?.balance ??
                        userData?.points ??
                        userData?.balance ??
                        null;
                    pointsData = localPoints === null ? null : { balance: localPoints, segmentId };
                }

                // ==================== LEARNWORLDS ====================
                const eligible = isLearnworldsEligible(userData);
                setLearnwordsEligible(eligible);

                const coursesData = eligible ? await (async () => {
                        const keycloakId = extractKeycloakId(userData);
                        const email = userData?.email;

                        // Preferir Keycloak ID si está disponible
                        try {
                            if (keycloakId) {
                                const d = await LiceosAPI.getLearnworldsUserCoursesByKeycloakId(keycloakId, { forceSync: true });
                                if (d && d.success === false && d.message === 'Ruta no encontrada') return null;
                                return d;
                            }
                        } catch (err) {
                            if (err?.response?.status !== 404) {
                                console.warn('⚠️ Error al obtener cursos Learnworlds por Keycloak ID:', err?.response?.status || err);
                            }
                        }

                        // Fallback por email
                        try {
                            if (email) {
                                const d = await LiceosAPI.getLearnworldsUserCoursesByEmail(email, { forceSync: true });
                                // Si el backend responde success:false con 404 interno, lo tratamos como "sin cursos"
                                if (d && d.success === false) return [];
                                return d;
                            }
                        } catch (err) {
                            if (err?.response?.status === 404) return null;
                            console.warn('⚠️ Error al obtener cursos Learnworlds por email:', err?.response?.status || err);
                            return [];
                        }

                        // Sin identificadores para Learnworlds
                        return [];
                    })() : null;

                setPoints(pointsData);
                // Si coursesData === null => integración no disponible
                if (coursesData === null) {
                    setLearnwordsAvailable(false);
                    setLearnwordsCourses([]);
                } else {
                    setLearnwordsAvailable(true);
                    // Intentar normalizar formatos comunes:
                    // - Array directo
                    // - {courses: []}
                    // - {data: []}
                    // - {items: []}
                    const normalized =
                        Array.isArray(coursesData) ? coursesData :
                        Array.isArray(coursesData?.courses) ? coursesData.courses :
                        Array.isArray(coursesData?.data) ? coursesData.data :
                        Array.isArray(coursesData?.items) ? coursesData.items :
                        [];
                    setLearnwordsCourses(normalized);
                }
            } catch (err) {
                console.error('Error al cargar datos del usuario:', err);
                setLocalError('Error al cargar los datos del usuario');
            } finally {
                setLoading(false);
            }
        };
        
        loadUserData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, liceoId]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const normalizeUserType = (value) => {
        if (value === null || value === undefined) return null;
        // Si viene un objeto (muy común), intentar extraer propiedades típicas
        if (typeof value === 'object') {
            const v = value.name ?? value.code ?? value.type ?? value.id ?? null;
            return normalizeUserType(v);
        }
        if (typeof value === 'string') return value.toLowerCase().trim();
        return String(value).toLowerCase().trim();
    };

    const normalizeKeycloakAttributeValue = (value) => {
        // Keycloak attributes suele venir como { key: ["value"] } o { key: "value" }
        if (value === null || value === undefined) return null;
        if (Array.isArray(value)) return value.length ? value[0] : null;
        return value;
    };

    const inferUserTypeFromAttributes = (attrs) => {
        if (!attrs || typeof attrs !== 'object') return null;

        const getAttr = (key) => normalizeKeycloakAttributeValue(attrs[key]);

        const direct =
            getAttr('userType') ??
            getAttr('user_type') ??
            getAttr('type') ??
            getAttr('userTypeName') ??
            getAttr('user_type_name') ??
            getAttr('role') ??
            getAttr('roles');

        const normalizedDirect = normalizeUserType(direct);
        if (normalizedDirect) return normalizedDirect;

        // A veces vienen como grupos/perfiles/segmentos
        const groups = getAttr('groups') ?? getAttr('group') ?? null;
        const normalizedGroups = normalizeUserType(groups);
        if (normalizedGroups) return normalizedGroups;

        return null;
    };

    const inferUserTypeFromRoles = (u) => {
        const roles = Array.isArray(u?.roles)
            ? u.roles
            : Array.isArray(u?.role)
                ? u.role
                : u?.roles
                    ? [u.roles]
                    : u?.role
                        ? [u.role]
                        : [];
        const rolesUpper = roles.map(r => String(r).toUpperCase());

        if (rolesUpper.includes('COACH')) return 'coach';
        if (rolesUpper.includes('DIRECTOR')) return 'director';
        if (rolesUpper.includes('DOCENTE') || rolesUpper.includes('PROFESOR') || rolesUpper.includes('TEACHER')) return 'docente';
        if (rolesUpper.includes('ESTUDIANTE') || rolesUpper.includes('STUDENT')) return 'estudiante';
        if (rolesUpper.includes('POSTULANTE') || rolesUpper.includes('APPLICANT')) return 'postulante';
        return null;
    };

    const getUserTypeValue = (u) => {
        if (!u) return null;
        const direct =
            u.userType ??
            u.type ??
            u.user_type ??
            u.userTypeId ??
            u.user_type_id ??
            u.userTypeName ??
            u.user_type_name ??
            u.profile?.userType ??
            u.profile?.type ??
            null;

        const normalizedDirect = normalizeUserType(direct);
        if (normalizedDirect) return normalizedDirect;

        const attrType = inferUserTypeFromAttributes(u.attributes);
        if (attrType) return attrType;

        return inferUserTypeFromRoles(u);
    };

    const getUserTypeLabel = (type) => {
        const t = normalizeUserType(type);
        const labels = {
            estudiante: 'Estudiante',
            postulante: 'Postulante',
            docente: 'Docente',
            director: 'Director',
            coach: 'Coach'
        };
        return (t && labels[t]) ? labels[t] : (t || 'Desconocido');
    };

    const normalizeLearnworldsCourse = (raw) => {
        if (!raw || typeof raw !== 'object') return null;

        // Algunos backends devuelven un wrapper de progreso con { status, progress_rate, ... } y datos del curso anidados.
        const rawStatus = raw.status;
        const status =
            typeof rawStatus === 'string'
                ? rawStatus
                : typeof rawStatus?.status === 'string'
                    ? rawStatus.status
                    : typeof raw?.course_status === 'string'
                        ? raw.course_status
                        : null;

        const title =
            raw.name ||
            raw.title ||
            raw.course?.name ||
            raw.course?.title ||
            raw.course_title ||
            raw.courseName ||
            raw.courseTitle ||
            null;

        const description =
            raw.description ||
            raw.course?.description ||
            null;

        // progress_rate puede venir como 0..1 o 0..100
        const progressRateRaw =
            raw.progress ??
            raw.progress_rate ??
            rawStatus?.progress_rate ??
            raw.progressRate ??
            null;

        let progress = null;
        if (typeof progressRateRaw === 'number' && Number.isFinite(progressRateRaw)) {
            progress = progressRateRaw <= 1 ? Math.round(progressRateRaw * 100) : Math.round(progressRateRaw);
            progress = Math.max(0, Math.min(100, progress));
        }

        const totalUnits =
            raw.total_units ??
            raw.totalUnits ??
            rawStatus?.total_units ??
            null;

        const completedUnits =
            raw.completed_units ??
            raw.completedUnits ??
            rawStatus?.completed_units ??
            null;

        const id =
            raw.id ||
            raw._id ||
            raw.course_id ||
            raw.course?.id ||
            raw.course?._id ||
            raw.course?.course_id ||
            title ||
            JSON.stringify({ title, status, progress }).slice(0, 50);

        return {
            id,
            title,
            description,
            status,
            progress,
            totalUnits,
            completedUnits
        };
    };

    const getCourseStatusLabel = (status) => {
        if (!status) return 'Sin estado';
        const s = String(status);
        if (s === 'completed') return 'Completado';
        if (s === 'in_progress') return 'En progreso';
        if (s === 'not_started') return 'No iniciado';
        return s;
    };

    const getCourseStatusClass = (status) => {
        if (!status) return 'unknown';
        const s = String(status);
        if (s === 'completed') return 'completed';
        if (s === 'in_progress') return 'in_progress';
        if (s === 'not_started') return 'not_started';
        return 'unknown';
    };

    if (loading || isLoading) {
        return (
            <ToolbarComponent label="Detalle del Usuario" showBackButton={true} onBackClick={() => props.history.goBack()}>
                <Loader text="Cargando información del usuario..." />
            </ToolbarComponent>
        );
    }

    if (error || localError || !user) {
        return (
            <ToolbarComponent label="Detalle del Usuario" showBackButton={true} onBackClick={() => props.history.goBack()}>
                <div className="error-message">
                    {localError || error || 'No se pudo cargar la información del usuario'}
                </div>
            </ToolbarComponent>
        );
    }

    return (
        <ToolbarComponent label="Detalle del Usuario" showBackButton={true} onBackClick={() => props.history.goBack()}>
            <div className="user-detail-container">
                {/* Header */}
                <div className="detail-header">
                    <div>
                        <h1 className="detail-title">
                            {user.firstName || user.name?.split(' ')[0] || ''} {user.lastName || user.name?.split(' ').slice(1).join(' ') || ''}
                        </h1>
                        <p className="detail-subtitle">{user.email || '-'}</p>
                    </div>
                    {getUserTypeValue(user) && (
                        <span className="user-type-badge">
                            {getUserTypeLabel(getUserTypeValue(user))}
                        </span>
                    )}
                </div>

                {/* Información del Usuario */}
                <div className="detail-section">
                    <h2 className="section-title">Información Personal</h2>
                    <div className="info-grid">
                        <div className="info-item">
                            <span className="info-label">Nombre</span>
                            <span className="info-value">
                                {user.firstName || user.name?.split(' ')[0] || '-'}
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Apellido</span>
                            <span className="info-value">
                                {user.lastName || user.name?.split(' ').slice(1).join(' ') || '-'}
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Email</span>
                            <span className="info-value">{user.email || '-'}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Usuario</span>
                            <span className="info-value">{user.username || '-'}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Tipo de Usuario</span>
                            <span className="info-value">{getUserTypeLabel(getUserTypeValue(user))}</span>
                        </div>
                        {user.createdAt && (
                            <div className="info-item">
                                <span className="info-label">Fecha de Registro</span>
                                <span className="info-value">{formatDate(user.createdAt)}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Puntos */}
                <div className="detail-section">
                    <h2 className="section-title">Puntos</h2>
                    {points !== null ? (
                        <div className="points-container">
                            <div className="points-card">
                                <div className="points-value">
                                    {points.totalPoints !== undefined
                                        ? points.totalPoints
                                        : (points.points || points.balance || 0)}
                                </div>
                                <div className="points-label">
                                    Puntos Totales{points.segmentId ? ` (segmento ${points.segmentId})` : ''}
                                </div>
                            </div>
                            {points.segments && typeof points.segments === 'object' ? (
                                <div className="points-breakdown">
                                    {Object.entries(points.segments).map(([key, value]) => (
                                        <div key={key} className="points-item">
                                            <span className="points-item-label">{key}</span>
                                            <span className="points-item-value">{value}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : points.breakdown && (
                                <div className="points-breakdown">
                                    {Object.entries(points.breakdown).map(([key, value]) => (
                                        <div key={key} className="points-item">
                                            <span className="points-item-label">{key}</span>
                                            <span className="points-item-value">{value}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="no-data">No hay información de puntos disponible</div>
                    )}
                </div>

                {/* Cursos de Learnwords */}
                <div className="detail-section">
                    <h2 className="section-title">Cursos de Learnwords</h2>
                    {!learnwordsEligible ? (
                        <div className="no-data">Learnworlds solo aplica para estudiantes o postulantes</div>
                    ) : !learnwordsAvailable ? (
                        <div className="no-data">Integración de Learnwords no disponible</div>
                    ) : learnwordsCourses && learnwordsCourses.length > 0 ? (
                        <div className="courses-container">
                            {learnwordsCourses.map((rawCourse, index) => {
                                const course = normalizeLearnworldsCourse(rawCourse) || { id: index, title: null };
                                return (
                                <div key={course.id || index} className="course-card">
                                    <div className="course-header">
                                        <h3 className="course-title">{course.title || 'Curso sin nombre'}</h3>
                                        <span className={`course-status ${getCourseStatusClass(course.status)}`}>
                                            {getCourseStatusLabel(course.status)}
                                        </span>
                                    </div>
                                    {course.description && (
                                        <p className="course-description">{course.description}</p>
                                    )}
                                    <div className="course-info">
                                        {typeof course.progress === 'number' && (
                                            <div className="course-progress">
                                                <span>Progreso: {course.progress}%</span>
                                                <div className="progress-bar">
                                                    <div 
                                                        className="progress-fill" 
                                                        style={{ width: `${course.progress}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        )}
                                        {(course.completedUnits !== null || course.totalUnits !== null) && (
                                            <div className="course-stats">
                                                <span>Unidades: {course.completedUnits ?? '-'} / {course.totalUnits ?? '-'}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="no-data">El usuario no tiene cursos de Learnwords registrados</div>
                    )}
                </div>
            </div>
        </ToolbarComponent>
    );
};

export default UserDetailPage;


